import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import { toast } from 'react-toastify';
import "../Package/Package.css";     // Ensure correct path
import "../Package/PackageForm.css"; // Ensure correct path

const HolidaySightseeing = ({ holidayId, packageDetails, onUpdate }) => {
  const initialFieldValues = {
    packageSightSeeingId: "", // Will generate UUID for local identification
    date: "",
    destination: "",
    sightseeingName: "",
    description: "",
    price: "" 
  };

  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);

  // 1. Load and Parse Data from Prop (JSON String)
  useEffect(() => {
    if (packageDetails && packageDetails.holidaySightseeing) { // Note: Backend key might vary (holidaySightseeing vs holidaySightSeeing)
        try {
            const parsed = typeof packageDetails.holidaySightseeing === 'string'
                ? JSON.parse(packageDetails.holidaySightseeing)
                : packageDetails.holidaySightseeing;
            const mapped = parsed.map((item, index) => ({
                packageSightSeeingId: item.packageSightSeeingId || item.PackageSightSeeingId || `temp-${index}`,
                date: item.date || item.Date,
                destination: item.destination || item.Destination,
                sightseeingName: item.sightseeingName || item.SightseeingName,
                description: item.description || item.Description,
                price: item.price || item.Price
            }));
            
            // Sort by date
            mapped.sort((a, b) => new Date(a.date) - new Date(b.date));
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // --- CORE SAVE LOGIC (Updates Holiday JSON) ---
  const saveHolidayData = async (updatedList) => {
      const formData = new FormData();
      formData.append("holidayId", holidayId);
      // Backend usually expects 'holidaySightseeing' (check your exact column name)
      formData.append("holidaySightseeing", JSON.stringify(updatedList)); 
      
      if(user?.id) formData.append("userId", user.id);

      try {
          await apiService.update(config.UPDATEHOLIDAY, formData);
          if(onUpdate) {
              onUpdate(); 
          } else {
              setLists(updatedList);
          }
          return true;
      } catch (error) {
          console.error(error);
          toast.error("Failed to update sightseeing activities");
          return false;
      }
  };

  // --- HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);

      // 1. ID Management
      const currentId = values.packageSightSeeingId || crypto.randomUUID();
      const newItem = { ...values, packageSightSeeingId: currentId };

      // 2. Update Local Array
      let newList = [...lists];
      const index = newList.findIndex(x => x.packageSightSeeingId === currentId);

      if (index !== -1) {
          newList[index] = newItem; // Edit
      } else {
          newList.push(newItem); // Add
      }

      // 3. Save to Backend
      const success = await saveHolidayData(newList);
      if(success) {
          handleCancel();
      }
      setBtnLoading(false);
    }
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      const newList = lists.filter(x => x.packageSightSeeingId !== itemToDelete);
      const success = await saveHolidayData(newList);
      if(success) 
      handleCloseDeleteModal();
    }
  };

  // --- UI Helpers ---
  const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
  
  const handleCancel = () => { resetForm(); setIsFormVisible(false); };
  
  const showEditDetails = (data) => {
    const formattedData = {
        ...data,
        date: data.date ? moment(data.date).format('YYYY-MM-DDTHH:mm') : "",
    };
    setValues(formattedData);
    setIsFormVisible(true);
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setBtnLoading(false);
    setErrors({});
  };

  const validate = () => {
    let temp = {};
    temp.date = values.date !== "";
    temp.destination = values.destination !== "";
    temp.sightseeingName = values.sightseeingName !== "";
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setItemToDelete(null); };
  const applyErrorClass = (field) => (errors[field] === false ? " form-control-danger" : "");

  return (
    <div className="PackageSightseeing-wrapper">
      <div className="card PackageSightseeing-card">
        <div className="card-body">
          <div className="PackageSightseeing-header">
            <h4 className="header-title">Sightseeing Activities</h4>
            {!isFormVisible && (
              <button
                type="button"
                className="btn btn-primary PackageSightseeing-btn-gradient"
                onClick={handleAddNewClick}
              >
                <i className="ri-add-line me-1"></i> Add Activity
              </button>
            )}
          </div>
          
          {isFormVisible && (
            <div className="PackageSightseeing-form-container">
              <h5 className="form-title">
                {values.packageSightSeeingId ? "Edit Activity Details" : "Add Activity Details"}
              </h5>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="row">
                   <div className="col-lg-3 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-camera-lens-line me-2"></i>Activity Name</label>
                      <input type="text" name="sightseeingName" value={values.sightseeingName} onChange={handleInputChange} className={"form-control" + applyErrorClass("sightseeingName")} placeholder="e.g. Burj Khalifa Tour"/>
                      {errors.sightseeingName === false && <div className="validationerror">Please enter a valid activity name</div>}
                  </div>
                   <div className="col-lg-3 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-map-pin-line me-2"></i>Destination / City</label>
                      <input type="text" name="destination" value={values.destination} onChange={handleInputChange} className={"form-control" + applyErrorClass("destination")} placeholder="e.g. Dubai"/>
                      {errors.destination === false && <div className="validationerror">Please enter a valid destination</div>}
                  </div>

                  <div className="col-lg-3 mb-3">
                       <label className="form-label d-flex align-items-center"><i className="ri-calendar-event-line me-2"></i>Date & Time</label>
                       <input type="datetime-local" name="date" value={values.date} onChange={handleInputChange} className={"form-control" + applyErrorClass("date")}/>
                        {errors.date === false && <div className="validationerror">Please select a valid date and time</div>}
                  </div>
                  <div className="col-lg-3 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-money-dollar-circle-line me-2"></i>Price</label>
                      <input type="number" name="price" value={values.price} onChange={handleInputChange} className={"form-control"} placeholder="e.g. 150"/>
                  </div>
                  <div className="col-lg-12 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-file-text-line me-2"></i>Description</label>
                      <textarea name="description" value={values.description} onChange={handleInputChange} className={"form-control"} placeholder="Add notes or details about the activity..." rows="3"></textarea>
                  </div>

                </div>
                <div className="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                        {btnLoading ? "Saving..." : (values.packageSightSeeingId ? "Update" : "Submit")}
                    </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="mt-4">
            {lists.length > 0 ? (
              <div className="PackageSightseeing-list">
                {lists.map((v, i) => (
                  <div key={v.packageSightSeeingId || i} className="PackageSightseeing-list-item">
                    <div className="item-icon">
                      <i className="ri-camera-lens-line"></i>
                    </div>
                    <div className="item-content">
                        <div className="item-header">
                            <h6 className="item-title">{v.sightseeingName}</h6>
                             {v.price && <h6 className="item-price">{`${v.price}`}</h6>}
                        </div>
                        <p className="item-description">{v.description || 'No description provided.'}</p>
                       <div className="item-destination d-flex align-items-center">
                          <i className="ri-map-pin-2-fill me-1"></i>
                          <span>{v.destination}</span>
                            
                          <div className="item-date ms-3 d-flex align-items-center">
                            <i className="ri-calendar-todo-line me-2"></i>
                            {moment(v.date).format("ddd, DD MMM YYYY, HH:mm")}
                          </div>
                        </div>
                    </div>
                    <div className="item-actions">
                        <button className="PackageSightseeing-action-btn edit" onClick={() => showEditDetails(v)}><i className="ri-pencil-fill"/></button>
                        <button className="PackageSightseeing-action-btn delete" onClick={() => handleOpenDeleteModal(v.packageSightSeeingId)}><i className="ri-delete-bin-fill"/></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
                <div className="PackageSightseeing-empty-state">
                    <i className="ri-compass-3-line empty-icon"></i>
                    <h5>No Sightseeing Activities Added</h5>
                    <p>Click "Add Activity" to include tours and attractions.</p>
                </div>
            )}
          </div>
        </div>
      </div>
      <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
    </div>
  );
};

export default HolidaySightseeing;